import { useAppConfig } from 'application/configuration'
import { useStore } from 'effector-react'
import { Sort } from 'features/cars-list/components/Sort'
import { SortASP } from 'features/cars-list/components/SortASP'
import { useListSmartContractAPI } from 'features/smart-contract/list/useListSmartContractAPI'
import { useEditTradeInText } from 'features/smart-contract/utils'
import { createMediaStyles } from 'lib/styled-helpers'
import { usePriceFormatter } from 'lib/use-price-formatter'
import { FC } from 'react'
import { useAVNConfig } from 'stages/AVN'
import styled from 'styled-components'
import { SkeletonBlock } from 'ui/skeleton-block'
import { Switch } from 'ui/switch'

import { getProjectName } from '../../../lib/get-project-name'
import {
  $chosenCreditFilterValues,
  $creditFilterEnabled,
  $creditFilterValues,
  setCreditFilterEnabled
} from '../../credit-filter-form/model'

export type Props = {
  openCreditFilterForm: () => void
}

export const CommercialFilters: FC<Props> = ({ openCreditFilterForm }) => {
  const {
    creditFilterForm: { i18n: creditFilterFormI18n },
    sorting,
    sortingASP,
    i18n: carsListI18n,
    smartContractInstalled: useSmartContract,
    isCreditFilterVisible,
    isTradeInVisible,
    isNew
  } = useAVNConfig()
  const { state, api, creditAvailable, tradeInAvailable } = useListSmartContractAPI()
  const smartContractI18n = useAppConfig().smartContract.i18n
  const creditFilterEnabled = useStore($creditFilterEnabled)
  const editTradeInText = useEditTradeInText({ state, i18n: smartContractI18n })

  const appliedCreditFilterValues = useStore($chosenCreditFilterValues)
  const commonI18n = useAppConfig().i18n.common
  const formatPrice = usePriceFormatter()

  return (
    <Wrapper>
      {sorting && sortingASP && (
        <SortWrapper>{isNew ? <Sort {...sorting} /> : <SortASP {...sortingASP} />}</SortWrapper>
      )}
      {useSmartContract && (
        <>
          {state
            ? creditAvailable &&
              isCreditFilterVisible && (
                <SwitchWrapper>
                  <Switch
                    mobileSpaceBetween={true}
                    onChange={(value) => {
                      if (appliedCreditFilterValues) {
                        setCreditFilterEnabled(value)
                      } else {
                        openCreditFilterForm()
                      }
                    }}
                    checked={creditFilterEnabled}
                  />

                  <Label>
                    {creditFilterFormI18n.creditFilter}
                    <EditText onClick={openCreditFilterForm}>
                      {appliedCreditFilterValues
                        ? `${commonI18n.to} ${formatPrice(appliedCreditFilterValues.creditPayment)}/${
                            commonI18n.monthForms[0]
                          }`
                        : creditFilterFormI18n.changeCreditParameters}
                    </EditText>
                  </Label>
                </SwitchWrapper>
              )
            : isCreditFilterVisible && (
                <SkeletonWrapper>
                  <SkeletonBlock width="100%" height="100%" />
                </SkeletonWrapper>
              )}
          {state ? (
            tradeInAvailable &&
            isTradeInVisible && (
              <SwitchWrapper>
                <Switch
                  mobileSpaceBetween={true}
                  onChange={(value) => {
                    if (value) {
                      if (state.tradeIn.behaviourByUser === 'not_required') {
                        api.enableTradeIn({ open: false })
                      } else {
                        api.enableTradeIn({ preventBack: true })
                      }
                    } else {
                      api.disableTradeIn()
                    }
                  }}
                  checked={state.tradeIn.behaviourByUser === 'applied'}
                />

                <Label>
                  {carsListI18n.tradeIn}
                  <EditText onClick={() => api.showTradeIn({ preventBack: true })}>{editTradeInText}</EditText>
                </Label>
              </SwitchWrapper>
            )
          ) : (
            <SkeletonWrapper>
              <SkeletonBlock width="100%" height="100%" />
            </SkeletonWrapper>
          )}
        </>
      )}
    </Wrapper>
  )
}

const Label = styled.p`
  margin: 0;
  ${({ theme }) => theme.textStyles.p2};
`

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 1rem 2rem;

  border-left: 1px solid ${(props) => props.theme.palette.grey[100]};
  border: 1px solid ${(props) => props.theme.palette.grey[100]};
  ${createMediaStyles(() => ({
    phone: 'flex-direction: row-reverse; justify-content: space-between;border-width:1px 0 0',
    tablet: 'flex-direction: row; justify-content: stretch;min-width: 20rem; border-width:1px 0 0 1px',
    desktop: 'border-width:0px 0 0 1px'
  }))}
`

const Wrapper = styled.div`
  display: grid;
  grid-gap: 1px;
  margin-bottom: 2.4rem;
  align-items: center;
  grid-auto-rows: 65px;
  background: ${({ theme }) => theme.palette.white};
  border-radius: ${() => getProjectName() === 'avito' && '12px'};

  ${createMediaStyles(() => ({
    phone: 'grid-template-columns: 1fr;',
    tablet: 'grid-template-columns: auto 1fr;',
    projectors: 'grid-template-columns: 1fr auto auto;'
  }))};

  // Нужно для случая, когда на таблете только один свитч отрисовывается
  & ${SwitchWrapper}:last-child:nth-child(2) {
    ${createMediaStyles(() => ({
      phone: 'grid-column: auto;',
      tablet: 'grid-column: 1 / 3;',
      projectors: 'grid-column: auto;'
    }))};
  }
`

const SortWrapper = styled.div`
  height: 100%;
  border-radius: ${() => getProjectName() === 'avito' && '12px'};

  ${createMediaStyles(() => ({
    phone: 'margin-top: 2px;',
    tablet: 'grid-column: 1 / 3; grid-row: 1 / 2; margin-top: 0;',
    projectors: 'grid-column: 1 / 2;'
  }))}
`

const SkeletonWrapper = styled.div`
  height: 100%;
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey[100]};
  border-radius: ${() => getProjectName() === 'avito' && '12px;'};

  ${createMediaStyles((props) => ({
    phone: 'width: 100%; border-width:1px 0 0',
    tablet:
      'width: 100%; min-width: 20rem; border-width:1px 0 0 1px; grid-column: 1 / 3; grid-row: 1 / 2; margin-top: 0;',
    desktop: 'border-width:0px 0 0 1px',
    projectors: 'width: 20rem'
  }))}
`

const EditText = styled.span`
  display: block;
  margin-top: 0.4rem;
  margin-bottom: 0;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`
